import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { queryClient } from '@/lib/queryClient';
import { ToastProvider } from '@/components/ui/Toast';
import { initSentry, SentryErrorBoundary } from '@/lib/sentry';
import { primeReviewTokenFromCurrentUrl } from '@/pages/onboardingReview/reviewToken';
import '@/i18n/i18n';
import './index.css';

// Remove one-time review secrets from the address bar before telemetry starts.
primeReviewTokenFromCurrentUrl();

// P5-9 / M7.6 — fire-and-forget Sentry init. No-op when
// VITE_SENTRY_DSN is not provided at build time.
initSentry();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root mount point missing in index.html');

type RootElement = HTMLElement & {
    __tajergoPlatformRoot?: ReactDOM.Root;
};

function FallbackUi(): JSX.Element {
    return (
        <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
            <h1 style={{ fontSize: 20, fontWeight: 600 }}>
                Platform admin needs a refresh
            </h1>
            <p style={{ marginTop: 12, color: '#475569' }}>
                The TajerGo platform admin hit an unexpected error. Our team has
                been notified. Refresh the page or contact support if it
                persists.
            </p>
        </div>
    );
}

const platformRootEl = rootEl as RootElement;
const platformRoot = platformRootEl.__tajergoPlatformRoot ?? ReactDOM.createRoot(platformRootEl);
platformRootEl.__tajergoPlatformRoot = platformRoot;

platformRoot.render(
    <React.StrictMode>
        <SentryErrorBoundary fallback={<FallbackUi />}>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <ToastProvider>
                        <App />
                    </ToastProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </SentryErrorBoundary>
    </React.StrictMode>,
);
import { initializeInstall } from '../../packages/browser-app/install';
if (import.meta.env.PROD) initializeInstall('/sw.js', { reloadOnUpdate: true });
